import { useEffect, useMemo } from 'react';
import { css } from '@emotion/react';
import { useStore } from '@tanstack/react-form';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { Text } from '@frontend/design-system';
import { formatFieldLabel } from '../../../../../../helpers';
import type { DynamicFormApi } from '../../hooks/use-app-form';
import { DynamicFormField } from '../../types';
import { InsurerDropdown } from './insurer-dropdown';

export interface InsuranceVerificationFieldProps {
  form: DynamicFormApi;
  fieldInfo: DynamicFormField;
}

// TODO: This field doesn't currently trigger conditional logic (show/hide is handled by `ConditionalField`).

export const InsuranceVerificationField = ({ form, fieldInfo }: InsuranceVerificationFieldProps) => {
  const { required, label: fieldLabel } = fieldInfo;
  const { t } = useTranslation('digital-forms-experience');

  const relationshipOptions = [
    { label: t('Self'), value: 'self' },
    { label: t('Spouse'), value: 'spouse' },
    { label: t('Child'), value: 'child' },
    { label: t('Dependent'), value: 'dependent' },
  ];

  const requiredValidator = (value: unknown) => {
    if (!String(value ?? '').trim()) return t('This field is required');
    return undefined;
  };

  const relationship = useStore(
    form.store,
    (state) => (state.values[fieldInfo.key] as { relationship?: string } | undefined)?.relationship
  );

  const showSubscriberFields = !!relationship && relationship !== 'self';

  const subscriberFieldNames = useMemo(
    () =>
      (['subscriberMemberId', 'subscriberFirstName', 'subscriberLastName', 'subscriberDob'] as const).map(
        (key) => `${fieldInfo.key}.${key}`
      ),
    [fieldInfo.key]
  );

  // TanStack Form keeps unmounted `AppField`s registered (with their last errors/values), so they
  // must be explicitly deleted or a stale "required" error can block submit after they're hidden.
  useEffect(() => {
    if (showSubscriberFields) return;
    subscriberFieldNames.forEach((name) => form.deleteField(name));
  }, [showSubscriberFields, subscriberFieldNames, form]);

  return (
    <fieldset css={fieldsetStyle} role='group' aria-required={required} aria-labelledby={`${fieldInfo.key}-label`}>
      <Text id={`${fieldInfo.key}-label`} css={fieldLabelStyle}>
        {formatFieldLabel(fieldLabel, required)}
      </Text>

      <div css={fieldsContainerStyle}>
        <InsurerDropdown form={form} fieldKey={fieldInfo.key} required={!!required} />
        <form.AppField
          name={`${fieldInfo.key}.memberId`}
          validators={{
            onChange: ({ value }) => (required ? requiredValidator(value) : undefined),
          }}
        >
          {(field) => (
            <field.FormField
              label={formatFieldLabel(t('Member ID'), required)}
              type='text'
              fieldKey={`${fieldInfo.key}.memberId`}
              keepInlineLabel
            />
          )}
        </form.AppField>
        <form.AppField name={`${fieldInfo.key}.groupNo`}>
          {(field) => (
            <field.FormField
              label={formatFieldLabel(t('Group No'), false)}
              type='text'
              fieldKey={`${fieldInfo.key}.groupNo`}
              keepInlineLabel
            />
          )}
        </form.AppField>
        <form.AppField name={`${fieldInfo.key}.groupName`}>
          {(field) => (
            <field.FormField
              label={formatFieldLabel(t('Group Name'), false)}
              type='text'
              fieldKey={`${fieldInfo.key}.groupName`}
              keepInlineLabel
            />
          )}
        </form.AppField>
        <form.AppField
          name={`${fieldInfo.key}.firstName`}
          validators={{
            onChange: ({ value }) => (required ? requiredValidator(value) : undefined),
          }}
        >
          {(field) => (
            <field.FormField
              label={formatFieldLabel(t('First Name'), required)}
              type='text'
              fieldKey={`${fieldInfo.key}.firstName`}
              keepInlineLabel
            />
          )}
        </form.AppField>
        <form.AppField
          name={`${fieldInfo.key}.lastName`}
          validators={{
            onChange: ({ value }) => (required ? requiredValidator(value) : undefined),
          }}
        >
          {(field) => (
            <field.FormField
              label={formatFieldLabel(t('Last Name'), required)}
              type='text'
              fieldKey={`${fieldInfo.key}.lastName`}
              keepInlineLabel
            />
          )}
        </form.AppField>
        <form.AppField
          name={`${fieldInfo.key}.dob`}
          validators={{
            onChange: ({ value }) => (required ? requiredValidator(value) : undefined),
          }}
        >
          {(field) => (
            <field.FormField
              label={formatFieldLabel(t('Date of Birth'), required)}
              type='date'
              fieldKey={`${fieldInfo.key}.dob`}
              keepInlineLabel
            />
          )}
        </form.AppField>
        <form.AppField
          name={`${fieldInfo.key}.relationship`}
          validators={{
            onChange: ({ value }) => (required ? requiredValidator(value) : undefined),
          }}
        >
          {(field) => (
            <field.FormField
              label={formatFieldLabel(t('Relationship to Subscriber'), required)}
              type='select'
              fieldKey={`${fieldInfo.key}.relationship`}
              options={relationshipOptions}
              keepInlineLabel
            />
          )}
        </form.AppField>
        {/* Subscriber fields — mounted only when relationship is not 'self'; deleted from form state otherwise */}
        {showSubscriberFields && (
          <>
            <form.AppField
              name={`${fieldInfo.key}.subscriberMemberId`}
              validators={{
                onChange: ({ value }) => (required ? requiredValidator(value) : undefined),
              }}
            >
              {(field) => (
                <field.FormField
                  label={formatFieldLabel(t('Subscriber Member ID'), required)}
                  type='text'
                  fieldKey={`${fieldInfo.key}.subscriberMemberId`}
                  keepInlineLabel
                />
              )}
            </form.AppField>
            <form.AppField
              name={`${fieldInfo.key}.subscriberFirstName`}
              validators={{
                onChange: ({ value }) => (required ? requiredValidator(value) : undefined),
              }}
            >
              {(field) => (
                <field.FormField
                  label={formatFieldLabel(t('Subscriber First Name'), required)}
                  type='text'
                  fieldKey={`${fieldInfo.key}.subscriberFirstName`}
                  keepInlineLabel
                />
              )}
            </form.AppField>
            <form.AppField
              name={`${fieldInfo.key}.subscriberLastName`}
              validators={{
                onChange: ({ value }) => (required ? requiredValidator(value) : undefined),
              }}
            >
              {(field) => (
                <field.FormField
                  label={formatFieldLabel(t('Subscriber Last Name'), required)}
                  type='text'
                  fieldKey={`${fieldInfo.key}.subscriberLastName`}
                  keepInlineLabel
                />
              )}
            </form.AppField>
            <form.AppField
              name={`${fieldInfo.key}.subscriberDob`}
              validators={{
                onChange: ({ value }) => (required ? requiredValidator(value) : undefined),
              }}
            >
              {(field) => (
                <field.FormField
                  label={formatFieldLabel(t('Subscriber Date of Birth'), required)}
                  type='date'
                  fieldKey={`${fieldInfo.key}.subscriberDob`}
                  keepInlineLabel
                />
              )}
            </form.AppField>
          </>
        )}
      </div>
    </fieldset>
  );
};

const fieldsetStyle = css`
  border: none;
  margin: 0;
  padding: 0;
  min-width: 0;
`;

const fieldsContainerStyle = css`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing(2)};
`;

const fieldLabelStyle = css`
  width: 100%;
  margin-bottom: ${theme.spacing(0.5)};
`;
